import React, {useCallback, useEffect, useState} from "react";
import {Switch} from "antd";
import {useTypedSelector} from "migration/shared/lib/hooks/useTypedSelector";
import {Notify} from "migration/shared/lib/notification/notification";
import {useGlobalContext, useUserContext} from "../../../../../../../hooks/globalContext";
import {FormWithLoader} from "../../../../../../../components/loader/formWithLoader";
import IikoService, {ExtMenuModifierGroupInfo} from "../../api/iikoService";

export function IikoExtMenuModifierStopList() {
    const {i18n} = useGlobalContext();
    const {currentMenuId, user} = useUserContext();
    const {iikoMenuSettings} = useTypedSelector(state => state.iiko);

    const [isLoading, setIsLoading] = useState(false);
    const [groups, setGroups] = useState<ExtMenuModifierGroupInfo[]>([]);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [stoppedGroups, setStoppedGroups] = useState<Set<string>>(new Set());
    const [stoppedItems, setStoppedItems] = useState<Set<string>>(new Set());
    const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

    const fetchStopList = useCallback(async (controller: AbortController) => {
        if (!iikoMenuSettings?.iikoExternalMenuId || !iikoMenuSettings?.iikoOrganizationId) return;

        setIsLoading(true);
        try {
            const res = await IikoService.getIikoExtMenuModifierStopList({
                organizationId: user?.org?.id || "",
                menuId: currentMenuId,
                iikoOrganizationId: iikoMenuSettings.iikoOrganizationId,
                iikoExternalMenuId: iikoMenuSettings.iikoExternalMenuId,
                iikoPriceCategoryId: iikoMenuSettings.iikoPriceCategoryId ?? null,
            }, controller);

            if (res.data.success) {
                const data = res.data.data as {modifierGroups: ExtMenuModifierGroupInfo[]};
                setGroups(data.modifierGroups || []);
                setStoppedGroups(new Set(
                    (data.modifierGroups || []).filter(g => g.isStopped).map(g => g.sku)
                ));
                setStoppedItems(new Set(
                    (data.modifierGroups || []).flatMap(g =>
                        g.items.filter(it => it.isStopped).map(it => it.itemId)
                    )
                ));
            } else {
                Notify.Warning({
                    title: (res.data as any)?.error?.message || "Error",
                    message: "",
                });
            }
        } catch (e: any) {
            if (e?.name !== "CanceledError") {
                Notify.Warning({title: "Error", message: ""});
            }
        } finally {
            setIsLoading(false);
        }
    }, [iikoMenuSettings?.iikoExternalMenuId, iikoMenuSettings?.iikoOrganizationId, iikoMenuSettings?.iikoPriceCategoryId, user?.org?.id, currentMenuId]);

    useEffect(() => {
        const controller = new AbortController();
        fetchStopList(controller);
        return () => controller.abort();
    }, [fetchStopList]);

    const toggleExpand = useCallback((sku: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(sku)) next.delete(sku);
            else next.add(sku);
            return next;
        });
    }, []);

    const addLoadingId = (id: string) => setLoadingIds(prev => new Set(prev).add(id));
    const removeLoadingId = (id: string) => setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
    });

    const handleToggleGroup = async (groupSku: string, available: boolean) => {
        addLoadingId(groupSku);
        try {
            const req = {
                organizationId: user?.org?.id || "",
                menuId: currentMenuId,
                groupSku,
            };
            const res = available
                ? await IikoService.cmdRemoveExtMenuModifierGroupFromStopList(req)
                : await IikoService.cmdAddExtMenuModifierGroupToStopList(req);

            if (res.data.success) {
                if (available) {
                    setStoppedGroups(prev => {
                        const next = new Set(prev);
                        next.delete(groupSku);
                        return next;
                    });
                } else {
                    setStoppedGroups(prev => new Set(prev).add(groupSku));
                }
            } else {
                Notify.Warning({
                    title: (res.data as any)?.error?.message || "Error",
                    message: "",
                });
            }
        } catch {
            Notify.Warning({title: "Error", message: ""});
        } finally {
            removeLoadingId(groupSku);
        }
    };

    const handleToggleItem = async (itemId: string, available: boolean) => {
        addLoadingId(itemId);
        try {
            const req = {
                organizationId: user?.org?.id || "",
                menuId: currentMenuId,
                itemId,
            };
            const res = available
                ? await IikoService.cmdRemoveExtMenuModifierItemFromStopList(req)
                : await IikoService.cmdAddExtMenuModifierItemToStopList(req);

            if (res.data.success) {
                if (available) {
                    setStoppedItems(prev => {
                        const next = new Set(prev);
                        next.delete(itemId);
                        return next;
                    });
                } else {
                    setStoppedItems(prev => new Set(prev).add(itemId));
                }
            } else {
                Notify.Warning({
                    title: (res.data as any)?.error?.message || "Error",
                    message: "",
                });
            }
        } catch {
            Notify.Warning({title: "Error", message: ""});
        } finally {
            removeLoadingId(itemId);
        }
    };

    return (
        <FormWithLoader
            isloading={isLoading}
            id="extMenuModifierStopList"
            className={"form"}
            style={{padding: "0"}}
        >
            <h3>{i18n.modifier_visibility_settings()}</h3>

            {groups.length === 0 && !isLoading && (
                <div className="alert alert-info py-4">
                    {i18n.no_group_modifiers_found()}
                </div>
            )}

            <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
                {groups.map(group => {
                    const isGroupStopped = stoppedGroups.has(group.sku);
                    const isExpanded = expandedGroups.has(group.sku);

                    return (
                        <div
                            key={group.sku}
                            style={{
                                border: "1px solid #e8e8e8",
                                borderRadius: "8px",
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "12px 16px",
                                    backgroundColor: "#fafafa",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        flex: 1,
                                        cursor: "pointer",
                                    }}
                                    onClick={() => toggleExpand(group.sku)}
                                >
                                    <span
                                        style={{
                                            fontSize: "12px",
                                            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                            transition: "transform 0.2s",
                                        }}
                                    >
                                        ▶
                                    </span>
                                    <span style={{fontWeight: 500}}>{group.name}</span>
                                    <span style={{color: "#999", fontSize: "12px"}}>
                                        ({group.items.length})
                                    </span>
                                </div>

                                <Switch
                                    checked={!isGroupStopped}
                                    loading={loadingIds.has(group.sku)}
                                    onChange={(checked) => handleToggleGroup(group.sku, checked)}
                                />
                            </div>

                            {isExpanded && group.items.length > 0 && (
                                <div style={{padding: "0 16px 12px 40px"}}>
                                    {group.items.map((item, index) => {
                                        const isItemStopped = stoppedItems.has(item.itemId);
                                        const isLast = index === group.items.length - 1;

                                        return (
                                            <div
                                                key={item.itemId}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    padding: "8px 0",
                                                    borderBottom: isLast ? "none" : "1px solid #f0f0f0",
                                                    opacity: isGroupStopped ? 0.5 : 1,
                                                }}
                                            >
                                                <span style={{display: "flex", alignItems: "center", gap: "8px"}}>
                                                    <span style={{color: "#ccc", fontFamily: "monospace"}}>
                                                        {isLast ? "└──" : "├──"}
                                                    </span>
                                                    {item.name}
                                                </span>

                                                <Switch
                                                    checked={!isItemStopped}
                                                    loading={loadingIds.has(item.itemId)}
                                                    disabled={isGroupStopped}
                                                    onChange={(checked) => handleToggleItem(item.itemId, checked)}
                                                    size="small"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </FormWithLoader>
    );
}
